import { useEffect, useRef, useState } from "react";
import BaseDialog, { Field, FieldSet } from "../../modelDialogs/BaseDialog";
import { newId } from "../../models/BaseObject";
import { UserReference } from "../../models/ProjectMetadata";
import { saveProject } from "../SaveProject";
import { showToast } from "../Toasts";
import { itx } from "../../translationItx/itx";
import ProjectLicense from "../../models/ProjectLicense";

// Saves the currently open project as a new, independent copy under a new name,
// then continues editing that copy (like "Save As" in a desktop application).
export default function SaveAsDialog() {
  const originalName = state.projectMetadata.name;
  const [name, setName] = useState(() => state.noDoubleValue.getCopiedStringText(originalName));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.select();
  }, []);

  function existingProjectNames(): Array<string> {
    const names: Array<string> = [];
    state.allProjectsMap.forEach((project) => {
      if (project.id != state.projectMetadata.id) {
        names.push(project.name);
      }
    });
    return names;
  }

  return (
    <BaseDialog
      title={itx("saveAsDialog.title", "Speichern unter")}
      okCaption={itx("saveAsDialog.save", "Speichern")}
      enterAsOk={true}
      onClose={() => true}
      onOk={() => {
        const trimmedName = name.trim();
        if (!trimmedName) {
          showToast({
            isError: true,
            text: itx("saveAsDialog.pleaseEnterAName", "Bitte geben Sie einen Namen ein"),
          });
          return false;
        }

        let projectName = trimmedName;
        const existingNames = existingProjectNames();
        while (state.noDoubleValue.doubleName(projectName, existingNames)) {
          projectName = state.noDoubleValue.getCopiedStringText(projectName);
        }

        // detach the in-memory project from its original id so it is saved as a new project
        state.projectMetadata.id = newId();
        state.root.id = state.projectMetadata.id;
        state.projectMetadata.name = projectName;
        state.projectMetadata.users = new Array<UserReference>();
        state.projectMetadata.setUserForNewProject();

        // reset usage/history data so the copy starts fresh, as if newly created
        const now = new Date();
        state.projectMetadata.created = now;
        state.projectMetadata.lastModifiedOnServer = now;
        state.projectMetadata.lastModifiedLocal = undefined;
        state.projectMetadata.opendDateTime = now;
        state.projectMetadata.statistic.hoursInEdit = 0;
        state.projectMetadata.persistantAssistanceData.projectOpenCount = 0;

        state.projectMetadata.localCopied = undefined;
        state.projectMetadata.readOnly = false;
        state.allProjectsMap.set(state.projectMetadata.id, state.projectMetadata);

        // for license handeling
        state.projectMetadata.projectLicence = {
          invoiceIds: [] as Array<string>,
        };
        // the actual licenses live on state.projectLicense (not on projectMetadata.projectLicence),
        // so they must be reset here too, otherwise the copy inherits the original project's licenses
        state.projectLicense = new ProjectLicense({}, state.root);

        saveProject();

        return true;
      }}
    >
      <div style={{ padding: "16px", minWidth: "380px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "14px",
            background: "var(--panel-background)",
            borderRadius: "var(--panel-border-radius)",
            boxShadow: "var(--panel-shadow)",
            padding: "12px 16px",
            marginBottom: "18px",
          }}
        >
          <i className="las la-copy" style={{ fontSize: "2.4em", color: "var(--primary-color)" }}></i>
          <div>
            <div style={{ fontWeight: 600 }}>{itx("saveAsDialog.createCopy", "Kopie des Projekts erstellen")}</div>
            <div style={{ color: "grey", fontSize: "0.9em", marginTop: "2px" }}>
              {itx("saveAsDialog.currentProjectT1", `Aktuelles Projekt: "T1"`, { T1: originalName })}
            </div>
          </div>
        </div>

        <FieldSet>
          <Field label={itx("saveAsDialog.name", "Name") + ":"}>
            <input
              ref={inputRef}
              className="input"
              style={{ width: "100%" }}
              autoFocus
              maxLength={state.maxCharLimit.text}
              value={name}
              onChange={(ev) => setName(ev.target.value)}
            />
          </Field>
        </FieldSet>

        <div style={{ color: "grey", fontSize: "0.85em", marginTop: "12px" }}>
          {itx(
            "saveAsDialog.explanation1",
            "Das Projekt wird unter dem neuen Namen als eigenständige Kopie gespeichert.",
          )}
          <br />
          {itx("saveAsDialog.explanation2", "Sie arbeiten anschließend in dieser Kopie weiter.")}
        </div>
      </div>
    </BaseDialog>
  );
}
